import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Play, Pause, MusicNotesSimple, ArrowLeft, WhatsappLogo } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public catalog (no auth). Anyone with the URL can listen to all paid songs
 * grouped/filterable by genre. Privacy: only public fields (title, artist
 * display name, genre, cover, audio) are returned by the backend.
 */
export default function Catalogo() {
  const [params, setParams] = useSearchParams();
  const initialGenre = params.get("g") || "";

  const [genres, setGenres] = useState([]);
  const [genre, setGenre] = useState(initialGenre);
  const [total, setTotal] = useState(0);
  const [songs, setSongs] = useState([]);
  const [loading, setLoading] = useState(true);

  const audioRef = useRef(null);
  const [playingId, setPlayingId] = useState(null);

  // Load genres + counts once
  useEffect(() => {
    axios.get(`${API}/public/catalog/genres`)
      .then(r => { setGenres(r.data.genres); setTotal(r.data.total); })
      .catch(() => {});
  }, []);

  // Load songs whenever genre filter changes
  useEffect(() => {
    setLoading(true);
    const q = genre ? `?genre=${genre}` : "";
    axios.get(`${API}/public/catalog/songs${q}`)
      .then(r => setSongs(r.data.songs))
      .catch(() => setSongs([]))
      .finally(() => setLoading(false));

    // Sync URL ?g=
    if (genre) setParams({ g: genre }, { replace: true });
    else setParams({}, { replace: true });
  }, [genre, setParams]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onEnded = () => setPlayingId(null);
    a.addEventListener("ended", onEnded);
    return () => a.removeEventListener("ended", onEnded);
  }, []);

  const toggle = (song) => {
    const a = audioRef.current;
    if (!a) return;
    if (playingId === song.id) {
      a.pause();
      setPlayingId(null);
      return;
    }
    a.src = song.audio_url;
    a.volume = 0.85;
    a.play().then(() => setPlayingId(song.id)).catch(() => setPlayingId(null));
  };

  return (
    <div className="min-h-screen bg-[#050505] notranslate" translate="no">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-lg bg-[#050505]/80 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white" data-testid="catalog-home">
            <div className="w-9 h-9 rounded-xl bg-[#FFB800] flex items-center justify-center">
              <MusicNotesSimple weight="fill" className="text-black" size={18} />
            </div>
            <span className="font-display font-bold text-lg">Tu MusiKa</span>
          </Link>
          <Link to="/" className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1.5" data-testid="catalog-back-home">
            <ArrowLeft size={14} /> Volver al inicio
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="mb-6">
          <p className="text-sm uppercase tracking-widest text-[#FFB800] mb-2">Catálogo público</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
            Música hecha por la gente.
          </h1>
          <p className="text-white/60 mt-3 max-w-xl">
            Explora <span className="text-white font-semibold">{total}</span> canciones creadas en Tu MusiKa. Filtra por género y dale play.
          </p>
        </div>

        {/* Genre tabs */}
        <div className="flex gap-2 overflow-x-auto pb-3 mb-6 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="genre-tabs">
          <button
            onClick={() => setGenre("")}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${genre === "" ? "bg-[#FFB800] text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
            data-testid="genre-tab-all"
          >
            Todos · {total}
          </button>
          {genres.map(g => (
            <button
              key={g.key}
              onClick={() => setGenre(g.key)}
              disabled={g.count === 0}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all disabled:opacity-30 disabled:cursor-not-allowed ${genre === g.key ? "bg-[#FFB800] text-black" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
              data-testid={`genre-tab-${g.key}`}
            >
              {g.name} · {g.count}
            </button>
          ))}
        </div>

        {/* Songs grid */}
        {loading ? (
          <div className="py-20 text-center text-white/50">Cargando canciones…</div>
        ) : songs.length === 0 ? (
          <div className="py-20 text-center" data-testid="catalog-empty">
            <p className="text-white/60">Aún no hay canciones en este género.</p>
            <Link to="/register" className="inline-block mt-4 px-6 py-3 rounded-full bg-[#B4F34A] text-black font-semibold">
              ¡Sé el primero en crear una!
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="catalog-songs">
            {songs.map(s => {
              const isPlaying = playingId === s.id;
              return (
                <article
                  key={s.id}
                  data-testid={`catalog-song-${s.id}`}
                  className={`group rounded-2xl border transition-all overflow-hidden ${isPlaying ? "bg-[#FFB800]/5 border-[#FFB800]/30" : "bg-[#0a0a0a] border-white/5 hover:border-white/15"}`}
                >
                  <div className="relative aspect-square bg-[#121212]">
                    {s.cover_url ? (
                      <img src={s.cover_url} alt={s.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <MusicNotesSimple size={48} className="text-white/20" weight="duotone" />
                      </div>
                    )}
                    <button
                      onClick={() => toggle(s)}
                      aria-label={isPlaying ? "Pausar" : "Reproducir"}
                      data-testid={`catalog-play-${s.id}`}
                      className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/50 transition-colors opacity-0 group-hover:opacity-100"
                      style={isPlaying ? { opacity: 1 } : {}}
                    >
                      <div className="w-14 h-14 rounded-full bg-[#FFB800] flex items-center justify-center">
                        {isPlaying ? <Pause size={24} weight="fill" className="text-black" /> : <Play size={24} weight="fill" className="text-black" />}
                      </div>
                    </button>
                  </div>
                  <div className="p-4">
                    <p className="font-display font-bold text-lg truncate">{s.title}</p>
                    <p className="text-sm text-white/50 truncate">Por {s.artist_name}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="inline-block text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-white/10 text-white/80">
                        {s.genre_name}
                      </span>
                      <button
                        onClick={() => {
                          const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/song/${s.id}`;
                          const text = encodeURIComponent(`🎵 Escucha "${s.title}" (${s.genre_name}) creada en Tu MusiKa:\n${shareUrl}`);
                          window.open(`https://wa.me/?text=${text}`, "_blank");
                        }}
                        aria-label="Compartir por WhatsApp"
                        className="w-8 h-8 rounded-full bg-[#25D366]/15 hover:bg-[#25D366] text-[#25D366] hover:text-black flex items-center justify-center transition-colors"
                        data-testid={`catalog-share-${s.id}`}
                      >
                        <WhatsappLogo weight="fill" size={16} />
                      </button>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <audio ref={audioRef} preload="none" />
      </main>

      <footer className="text-center text-xs text-white/30 py-10">
        Tu MusiKa · Catálogo público · Creado con tu historia.
      </footer>
    </div>
  );
}
